import { useSyncExternalStore } from "react";

const STORAGE_KEY = "gonovus_admin_theme";
const THEME_CHANGE = "gonovus-admin-theme-change";
const SYSTEM_QUERY = "(prefers-color-scheme: dark)";

export type Theme = "dark" | "light";
export type ThemePreference = Theme | "system";

let memoryPreference: ThemePreference = "light";

function readPreference(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "dark" || value === "system" ? value : "light";
  } catch {
    return memoryPreference;
  }
}

function readTheme(): Theme {
  const preference = readPreference();
  return preference === "system"
    ? window.matchMedia(SYSTEM_QUERY).matches
      ? "dark"
      : "light"
    : preference;
}

/** 实际配色写入 token 选择器；存储保留用户选择的 system，不写解析结果。 */
function syncTheme(): void {
  document.documentElement.dataset.theme = readTheme();
  window.dispatchEvent(new Event(THEME_CHANGE));
}

function setTheme(preference: ThemePreference): void {
  memoryPreference = preference;
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // iframe 隐私设置禁用存储时，本页仍可切换主题。
  }
  syncTheme();
}

/** 其他标签页修改或清空偏好时，同步文档和所有主题订阅者。 */
function syncStoredTheme(event: StorageEvent): void {
  try {
    if (
      event.storageArea === localStorage &&
      (event.key === STORAGE_KEY || event.key === null)
    ) {
      syncTheme();
    }
  } catch {
    // 禁用存储的浏览器没有可同步的持久化偏好。
  }
}

let systemQuery: MediaQueryList | undefined;

/** 启动时应用主题，加载页也跟随系统；重复初始化先移除旧监听。 */
export function applyStoredTheme(): void {
  window.addEventListener("storage", syncStoredTheme);
  systemQuery?.removeEventListener("change", syncTheme);
  if (typeof window.matchMedia === "function") {
    systemQuery = window.matchMedia(SYSTEM_QUERY);
    systemQuery.addEventListener("change", syncTheme);
  }
  syncTheme();
}

/** 导航和 Playground 订阅同一主题，避免各自持有一份状态。 */
function subscribeTheme(onChange: () => void): () => void {
  window.addEventListener(THEME_CHANGE, onChange);
  return () => {
    window.removeEventListener(THEME_CHANGE, onChange);
  };
}

export function useTheme(): {
  theme: Theme;
  preference: ThemePreference;
  setTheme: (preference: ThemePreference) => void;
} {
  const theme = useSyncExternalStore(subscribeTheme, readTheme);
  const preference = useSyncExternalStore(subscribeTheme, readPreference);
  return { theme, preference, setTheme };
}
